import { useState, useEffect, useRef } from "react";
import AdditionalChargesIcon from "../../SvgComponent/AdditionalCharges";
import styles from "../../../../../constants/styles.json";
import usePOSConfigStore from "../../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../../stores/useTransactionStore";
import useCartStore from "../../../../../stores/useCartStore";

/**
 * Hook for additional charges / TCS / rounding logic.
 *
 * @param {Object} deps
 * @param {Object}   deps.cart
 * @param {Function} deps.setCart
 * @param {number}   deps.pricePrecision
 * @param {Object}   deps.tillData
 * @param {Object}   deps.posConfig
 * @param {Object}   deps.themeJSON
 */
const useAdditionalCharges = ({ cart, setCart, pricePrecision, tillData, posConfig, themeJSON }) => {
  const previousTcsRef = useRef(0);
  const exchangeCart = useCartStore((s) => s.exchangeCart);

  // ── State ────────────────────────────────────────────────────────────
  const [additionalCharges, setAdditionalCharges] = useState(false);
  const [tempSaleTypeData, setTempSaleTypeData] = useState([]);
  const [serviceChargeEnabled, setServiceChargeEnabled] = useState(false);
  const [serviceChargePercentage, setServiceChargePercentage] = useState(5);
  const [tipMode, setTipMode] = useState("Percentage");
  const [tipPercentage, setTipPercentage] = useState(0);
  const [tipAmount, setTipAmount] = useState(0);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [packingCharge, setPackingCharge] = useState(0);
  const [tcsModalAmount, setTcsModalAmount] = useState(0);
  const [showTcsModal, setShowTcsModal] = useState(false);
  const [roundingOption, setRoundingOption] = useState("No Rounding");

  const [serviceChargeAmount, setServiceChargeAmount] = useState(() => {
    return cart?.serviceCharge || 0;
  });

  const [tipTotal, setTipTotal] = useState(() => {
    return cart?.tip || 0;
  });

  const [deliveryChargeAmount, setDeliveryChargeAmount] = useState(() => {
    return cart?.deliveryCharge || 0;
  });

  const [packingChargeAmount, setPackingChargeAmount] = useState(() => {
    return cart?.packingCharge || 0;
  });

  // ── Helpers ──────────────────────────────────────────────────────────

  const toFixedTruncate = (num, decimalPlaces = 3) => {
    if (isNaN(num) || num === null) num = 0;
    const factor = Math.pow(10, decimalPlaces);
    const truncatedNum = Math.trunc(num * factor) / factor;
    const result = truncatedNum.toFixed(decimalPlaces);
    return result;
  };

  const applyRounding = (value, precision = pricePrecision) => {
    if (isNaN(value) || value === null) return 0;
    const factor = Math.pow(10, precision);
    switch (roundingOption) {
      case "Round Up":
        return Math.ceil(value * factor) / factor;
      case "Round Down":
        return Math.floor(value * factor) / factor;
      case "No Rounding":
      default:
        return parseFloat(toFixedTruncate(value, precision));
    }
  };

  const compare = (lhs, operator, rhs) => {
    switch (operator) {
      case ">":
        return lhs > rhs;
      case "<":
        return lhs < rhs;
      case "=":
        return lhs == rhs;
      default:
        return false;
    }
  };

  const calculateCharge = (charge, subtotal) => {
    if (!charge) return 0;

    if (charge.value !== null) {
      if (charge.isPercentage) {
        return (subtotal * charge.value) / 100;
      }
      return charge.value;
    }

    // default fallback
    if (charge.sameTypeCount === 1) {
      if (charge.isPercentage && charge.defaultPercentage > 0) {
        return (subtotal * charge.defaultPercentage) / 100;
      }
      return charge.defaultAmount || 0;
    }

    return 0;
  };

  // ── Core functions ───────────────────────────────────────────────────

  const computeTCS = (cartData, tempSaleTypeData) => {
    if (!cartData?.customer) return { total: 0, eligible: false, items: null };

    const isNRI = cartData?.customer?.nriBilling === true;

    if (isNRI) {
      const tcsChargeNRI = tempSaleTypeData?.find((c) => c.chargeType === "TCS");
      if (!tcsChargeNRI?.conditionsFormula) return { total: 0, eligible: false, items: null };
      let nriThreshold = null;
      let nriRate = null;
      try {
        const formulaNRI = JSON.parse(tcsChargeNRI.conditionsFormula);
        const nriCond = formulaNRI?.applicability?.conditions?.find(
          (c) => c.when?.field === "customer.customer_type" && c.when?.operator === "IN" && c.when?.value?.includes("NRI")
        );
        if (nriCond) {
          nriThreshold = Number(nriCond.value) || null;
        }
        if (formulaNRI?.rate_determination?.type === "conditional") {
          const nriRateCond = formulaNRI.rate_determination.conditions?.find(
            (c) => c.when?.field === "customer.customer_type" && c.when?.operator === "IN" && c.when?.value?.includes("NRI")
          );
          if (nriRateCond) {
            nriRate = Number(nriRateCond.rate) || null;
          }
        }
        if (formulaNRI?.nri_rate !== undefined) {
          nriRate = Number(formulaNRI.nri_rate) || nriRate;
        }
        if (formulaNRI?.nri_threshold !== undefined) {
          nriThreshold = Number(formulaNRI.nri_threshold) || nriThreshold;
        }
      } catch {
        return { total: 0, eligible: false, items: null };
      }
      if (nriThreshold === null || nriRate === null) return { total: 0, eligible: false, items: null };

      const baseTotal = (cartData?.subTotal || 0) + (cartData?.tax || 0);

      if (baseTotal > nriThreshold) {
        const subtotal = cartData?.subTotal || 0;
        const nriTcs = applyRounding(subtotal * nriRate);

        return {
          total: nriTcs,
          eligible: true,
          items: null,
        };
      }

      return { total: 0, eligible: false, items: null };
    }
    if (!cartData?.customer?.customerProperties) return { total: 0, eligible: false, items: null };

    const tcsCharge = tempSaleTypeData?.find((c) => c.chargeType === "TCS");
    if (!tcsCharge || !tcsCharge.conditionsFormula) return { total: 0, eligible: false, items: null };

    let formula;
    try {
      formula = JSON.parse(tcsCharge.conditionsFormula);
    } catch {
      return { total: 0, eligible: false, items: null };
    }
    let rate = 0;
    const rateDetermination = formula?.rate_determination;
    const panStatus = cartData.customer.customerProperties?.realStatus?.trim()?.toUpperCase();
    if (rateDetermination?.type === "conditional") {
      for (const cond of rateDetermination.conditions || []) {
        const { when, rate: condRate } = cond;
        if (
          when.field === "customer.pan_status" &&
          when.operator === "IN" &&
          when.value
            ?.filter((v) => v !== null && v !== undefined)
            .map((v) => v.toString().trim().toUpperCase())
            .includes(panStatus)
        ) {
          rate = condRate;
          break;
        }
      }
    }

    if (rate === 0) return { total: 0, eligible: false, items: null };

    let totalTcs = 0;
    // Evaluate ALL applicability conditions, not just [0]
    const applicabilityConditions = formula?.applicability?.conditions || [];
    let applicability = null;
    for (const cond of applicabilityConditions) {
      if (!cond.when) {
        applicability = cond;
        break;
      }
      if (cond.when.field === "customer.customer_type" && cond.when.operator === "IN" && cond.when.value?.includes("NRI") && cartData?.customer?.nriBilling === true) {
        applicability = cond;
        break;
      }
    }
    if (!applicability) {
      // Fallback to first condition if no match
      applicability = applicabilityConditions[0];
    }
    if (!applicability) return { total: 0, eligible: false, items: null };

    let hasEligibleItems = false;
    let subtotalTrigger = false;
    // Build immutable updated items array (no direct mutation)
    const updatedItems = (cartData.items || []).map((item) => {
      const cleaned = { ...item };
      delete cleaned.itemTcs;
      return cleaned;
    });

    if (applicability?.field === "cart.total") {
      const cartTotal =
        (cartData?.subTotal || 0) +
        (cartData?.tax || 0) +
        (cartData?.serviceCharge || 0) +
        (cartData?.tip || 0) +
        (cartData?.deliveryCharge || 0) +
        (cartData?.others || 0) +
        (cartData?.packingCharge || 0);
      if (!compare(cartTotal, applicability.operator, applicability.value)) {
        return { total: 0, eligible: false, items: null };
      }
      updatedItems.forEach((item) => {
        const itemValue = item.lineGross || item.linegross || item.nettotal || 0;
        const itemTcs = applyRounding(itemValue * rate);
        totalTcs += itemTcs;
        item.itemTcs = itemTcs;
        hasEligibleItems = true;
      });
    } else if (applicability?.field === "cart.bill_item_value") {
      updatedItems.forEach((item) => {
        const itemValue = item.lineGross || item.linegross || parseFloat(item.realPrice || item.sunitprice || 0) * parseFloat(item.weight || 0) || 0;
        if (compare(itemValue, applicability.operator, applicability.value)) {
          hasEligibleItems = true;
          const itemTcs = applyRounding(itemValue * rate);
          totalTcs += itemTcs;
          item.itemTcs = itemTcs;
        }
      });
    } else {
      const subtotal = cartData?.subTotal || 0;
      if (compare(subtotal, applicability.operator, applicability.value)) {
        subtotalTrigger = true;
        updatedItems.forEach((item) => {
          const itemValue = item.lineGross || item.linegross || item.nettotal || 0;
          const itemTcs = applyRounding(itemValue * rate);
          totalTcs += itemTcs;
          item.itemTcs = itemTcs;
          hasEligibleItems = true;
        });
      }
    }

    return {
      total: applyRounding(totalTcs),
      eligible: hasEligibleItems || subtotalTrigger,
      items: hasEligibleItems ? updatedItems : null,
    };
  };

  const handleApplyCharges = () => {
    setAdditionalCharges(false);

    const chargeMap = {};

    tempSaleTypeData.forEach((charge, index, array) => {
      const chargeType = charge.chargeType;
      const sameTypeCount = array.filter((c) => c.chargeType === chargeType).length;

      let uiValue = null;

      if (chargeType === "SERVICE_CHARGE") {
        uiValue = serviceChargeEnabled ? serviceChargePercentage : null;
      }
      if (chargeType === "TIP") {
        uiValue = tipMode === "Percentage" ? tipPercentage : tipAmount;
      }
      if (chargeType === "DELIVERY_FEE") {
        uiValue = deliveryCharge;
      }
      if (chargeType === "PACKING_FEE") {
        uiValue = packingCharge;
      }

      chargeMap[chargeType] = {
        value: uiValue !== null && uiValue !== "" ? Number(uiValue) : null,
        defaultAmount: charge.defaultAmount,
        defaultPercentage: charge.defaultPercentage,
        isPercentage:
          (chargeType === "SERVICE_CHARGE" && serviceChargeEnabled) ||
          (chargeType === "TIP" && tipMode === "Percentage") ||
          (charge.defaultPercentage > 0 && charge.defaultAmount === 0),
        sameTypeCount: sameTypeCount,
      };
    });

    const subtotal = cart?.subTotal || 0;
    const tax = cart.tax || 0;

    const serviceChargeAmt = calculateCharge(chargeMap["SERVICE_CHARGE"], subtotal);
    const tipAmt = calculateCharge(chargeMap["TIP"], subtotal);
    const deliveryAmt = calculateCharge(chargeMap["DELIVERY_FEE"], subtotal);
    const packingAmt = calculateCharge(chargeMap["PACKING_FEE"], subtotal);

    const tempServiceCharge = applyRounding(serviceChargeAmt);
    const tempTip = applyRounding(tipAmt);
    const tempDelivery = applyRounding(deliveryAmt);
    const tempPacking = applyRounding(packingAmt);

    const updatedCart = {
      ...cart,
      serviceCharge: tempServiceCharge,
      tip: tempTip,
      deliveryCharge: tempDelivery,
      packingCharge: tempPacking,
      total: parseFloat(subtotal) + parseFloat(tax) + tempServiceCharge + tempTip + tempDelivery + tempPacking + (cart.tcs || 0),
    };

    setCart(updatedCart);
  };

  const getChargeLabel = (charge, cart) => {
    // Special case for TCS
    if (charge.chargeType === "TCS") {
      const tcsValue = cart?.tcs ? Number(cart.tcs).toFixed(2) : "0";
      return `TCS - ${tcsValue}`;
    }

    // Normal UI display name logic
    try {
      if (charge.conditionsFormula) {
        const parsed = JSON.parse(charge.conditionsFormula);
        if (parsed?.ui?.display_name) {
          return parsed.ui.display_name;
        }
      }
    } catch (err) {
      console.error("[AdditionalCharges] Failed to parse conditionsFormula for display name:", err);
    }

    // Fallback formatting
    const label = charge.chargeType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return label;
  };

  const renderAdditionalChargesButton = () => {
    if (!tempSaleTypeData || tempSaleTypeData.length === 0) return null;

    return (
      <div
        style={{
          width: "100%",
          marginBottom: "0.37rem",
        }}
      >
        <button
          className="cartMenuItems"
          id="sm-parked-bill"
          style={{
            backgroundColor: themeJSON.cartMenuItemsBGColor || "#2F3856",
            border: `1px solid ${themeJSON.cartMenuItemsBGColor || "#2F3856"}`,
            borderRadius: "7px",
            height: "9.5vh",
          }}
          onClick={() => setAdditionalCharges(true)}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <AdditionalChargesIcon color={"#fff"} />
            <span style={styles.screenStyles.mainScreen.posIcon}>Additional Charges</span>
          </div>
        </button>
      </div>
    );
  };

  // ── Effects ──────────────────────────────────────────────────────────

  // Poll saleTypeData from store and initialise tempSaleTypeData + totals
  useEffect(() => {
    const interval = setInterval(() => {
      const parsed = usePOSConfigStore.getState().saleTypeData;
      if (parsed) {
        let updatedTempSaleTypeData = parsed?.additionalCharges || [];
        if (updatedTempSaleTypeData?.length > 0) {
          const localCart = {
            ...cart,
            serviceCharge: cart.serviceCharge ?? 0,
            tip: cart.tip ?? 0,
            deliveryCharge: cart.deliveryCharge ?? 0,
            others: cart.others ?? 0,
            packingCharge: cart.packingCharge ?? 0,
            tcs: cart.tcs ?? 0,
            tax: cart.tax ?? 0,
            total: cart.total ?? 0,
          };
          updatedTempSaleTypeData = updatedTempSaleTypeData.map((charge, index, array) => {
            const sameTypeCount = array.filter((c) => c.chargeType === charge.chargeType).length;
            const subtotal = cart?.subTotal ?? 0;
            const localValue =
              localCart[
                charge.chargeType === "SERVICE_CHARGE"
                  ? "serviceCharge"
                  : charge.chargeType === "TIP"
                    ? "tip"
                    : charge.chargeType === "DELIVERY_FEE"
                      ? "deliveryCharge"
                      : charge.chargeType === "OTHERS"
                        ? "others"
                        : charge.chargeType === "PACKING_FEE"
                          ? "packingCharge"
                          : charge.chargeType === "TCS"
                            ? "tcs"
                            : null
              ] || 0;
            const isPercentageMatch = charge.defaultPercentage > 0 && Math.abs(localValue - (subtotal * charge.defaultPercentage) / 100) < 0.01;

            let newValue;
            if (localValue > 0) {
              newValue = isPercentageMatch ? charge.defaultPercentage : localValue;
            } else if (sameTypeCount > 1 && localValue === 0) {
              newValue = "";
            } else {
              newValue = charge.value ?? (charge.defaultAmount !== 0 ? charge.defaultAmount : charge.defaultPercentage);
            }

            switch (charge.chargeType) {
              case "SERVICE_CHARGE":
              case "TIP":
              case "DELIVERY_FEE":
              case "OTHERS":
              case "PACKING_FEE":
              case "TCS":
                return { ...charge, value: newValue };
              default:
                return charge;
            }
          });

          setTempSaleTypeData(updatedTempSaleTypeData);

          const discount = localCart.discount;
          let updatedTotal;

          if (localCart.isReturn === true) {
            const returnTotal = localCart.items.reduce((sum, item) => sum + (item.nettotal ?? 0), 0);
            const existingReturnTcs = localCart.tcs ? -Math.abs(localCart.tcs) : 0;

            const cleanedCart = {
              ...localCart,
              subTotal: returnTotal,
              tax: 0,
              serviceCharge: 0,
              tip: 0,
              deliveryCharge: 0,
              others: 0,
              packingCharge: 0,
              tcs: existingReturnTcs,
              total: returnTotal + existingReturnTcs,
            };
            setCart(cleanedCart);
            clearInterval(interval);
            return;
          } else {
            // Build total from base fields to avoid double-counting
            updatedTotal =
              (localCart.subTotal || 0) +
              (localCart.tax || 0) +
              localCart.serviceCharge +
              localCart.tip +
              localCart.deliveryCharge +
              localCart.others +
              localCart.packingCharge +
              (localCart.tcs ?? 0);
          }
          if (Math.abs((localCart.total || 0) - updatedTotal) > 0.01 && (localCart?.isReturn !== true || localCart?.isLayaway !== "Y")) {
            setCart({ ...localCart, total: updatedTotal });
          }
        }

        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [cart]);

  const selectedReturnType = useTransactionStore((s) => s.selectedReturnType);

  const activeCart = selectedReturnType === "EXCHANGE" ? exchangeCart : cart;
  // Recompute TCS and total whenever items / customer / charges change
  useEffect(() => {
    if (!Array.isArray(tempSaleTypeData) || tempSaleTypeData.length === 0) {
      return;
    }

    const selectedReturnType = useTransactionStore.getState().selectedReturnType;

    const isExchangeMode = selectedReturnType === "EXCHANGE";

    const activeCart = isExchangeMode ? useCartStore.getState().exchangeCart : useCartStore.getState().cart;

    if (!activeCart || !Array.isArray(activeCart.items)) {
      return;
    }

    if (activeCart?.isReturn) {
      return;
    }

    const { total: tcsValue, items: updatedItems } = computeTCS(activeCart, tempSaleTypeData);

    const updatedTotal =
      (activeCart.subTotal || 0) +
      (activeCart.tax || 0) +
      (activeCart.serviceCharge || 0) +
      (activeCart.tip || 0) +
      (activeCart.deliveryCharge || 0) +
      (activeCart.others || 0) +
      (activeCart.packingCharge || 0) +
      tcsValue;

    const tcsChanged = activeCart.tcs !== tcsValue;

    const totalChanged = Math.abs((activeCart.total || 0) - updatedTotal) > 0.01;

    if (!tcsChanged && !totalChanged) {
      return;
    }

    const updatedCart = {
      ...activeCart,
      items: updatedItems || activeCart.items,
      tcs: tcsValue,
      total: updatedTotal,
    };

    if (isExchangeMode) {
      useCartStore.getState().setExchangeCart(updatedCart);
    } else {
      setCart(updatedCart);
    }
  }, [
    activeCart?.items,
    activeCart?.subTotal,
    activeCart?.tax,
    activeCart?.discount,
    activeCart?.customer,
    activeCart?.serviceCharge,
    activeCart?.tip,
    activeCart?.deliveryCharge,
    activeCart?.others,
    activeCart?.packingCharge,
    tempSaleTypeData,
    selectedReturnType,
  ]);

  // Show TCS notification modal on change
  useEffect(() => {
    const selectedReturnType = useTransactionStore.getState().selectedReturnType;

    const activeCart = selectedReturnType === "EXCHANGE" ? useCartStore.getState().exchangeCart : useCartStore.getState().cart;

    if (!activeCart) return;

    if (activeCart?.isReturn) return;

    const { total, eligible } = computeTCS(activeCart, tempSaleTypeData);

    const prev = previousTcsRef.current;

    if (eligible && total > 0 && total !== prev) {
      setTcsModalAmount(total);

      setShowTcsModal(true);

      setTimeout(() => {
        setShowTcsModal(false);
      }, 5000);
    }

    previousTcsRef.current = total;
  }, [activeCart?.items, activeCart?.customer, activeCart?.subTotal, activeCart?.tax, activeCart?.discount, tempSaleTypeData, selectedReturnType]);

  // ── Return ───────────────────────────────────────────────────────────
  return {
    // state
    additionalCharges,
    setAdditionalCharges,
    tempSaleTypeData,
    setTempSaleTypeData,
    serviceChargeEnabled,
    setServiceChargeEnabled,
    serviceChargePercentage,
    setServiceChargePercentage,
    tipMode,
    setTipMode,
    tipPercentage,
    setTipPercentage,
    tipAmount,
    setTipAmount,
    deliveryCharge,
    setDeliveryCharge,
    packingCharge,
    setPackingCharge,
    tcsModalAmount,
    setTcsModalAmount,
    showTcsModal,
    setShowTcsModal,
    roundingOption,
    setRoundingOption,
    serviceChargeAmount,
    setServiceChargeAmount,
    tipTotal,
    setTipTotal,
    deliveryChargeAmount,
    setDeliveryChargeAmount,
    packingChargeAmount,
    setPackingChargeAmount,

    // functions
    toFixedTruncate,
    applyRounding,
    compare,
    calculateCharge,
    computeTCS,
    handleApplyCharges,
    getChargeLabel,
    renderAdditionalChargesButton,
  };
};

export default useAdditionalCharges;
